import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';
import AdminDataTable from '../../../shared/components/AdminDataTable';
import AdminSubNav from '../../../shared/components/AdminSubNav';
import AdminHeader from '../../../shared/components/AdminHeader';
import { useAdminUsersApi } from '../../../api';
import type { UserLegalAcceptance } from '../../../api';
// Note: useAdminCompany is available but users are fetched from API
// In production, filtering would be done server-side via query params

interface AdminUsersListProps {
  activeSubSection: string;
  onSubSectionChange: (subSection: string) => void;
  brand: BrandConfig;
  Icons: IconsType;
}

interface UserRow {
  id: string;
  user_id?: string;
  name: string;
  email: string;
  role: string;
  status: string;
  appMetadata?: {
    organization?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

type UserAction = 'approve' | 'reject' | 'suspend' | 'reactivate';

const UserStatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const config: Record<string, { bg: string; text: string }> = {
    pending: { bg: '#FEF3C7', text: '#92400E' },
    approved: { bg: '#D1FAE5', text: '#065F46' },
    suspended: { bg: '#FEE2E2', text: '#991B1B' },
  };
  const style = config[status] || config.pending;

  return (
    <span
      className="inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full capitalize"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
};

interface ConfirmModalProps {
  showConfirmModal: boolean;
  confirmAction: UserAction | null;
  selectedUser: UserRow | null;
  actionLoading: string | null;
  brand: BrandConfig;
  onCancel: () => void;
  onConfirm: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  showConfirmModal,
  confirmAction,
  selectedUser,
  actionLoading,
  brand,
  onCancel,
  onConfirm,
}) => {
  if (!showConfirmModal) return null;

  const actionConfig: Record<string, { title: string; message: string; buttonText: string; buttonColor: string }> = {
    reject: {
      title: 'Reject User',
      message: `Are you sure you want to reject ${selectedUser?.name || selectedUser?.email}? This will permanently delete their account.`,
      buttonText: 'Reject User',
      buttonColor: brand.colors.error || '#EF4444',
    },
    suspend: {
      title: 'Suspend User',
      message: `Are you sure you want to suspend ${selectedUser?.name || selectedUser?.email}? They will not be able to log in.`,
      buttonText: 'Suspend User',
      buttonColor: brand.colors.warning || '#F59E0B',
    },
  };

  const config = actionConfig[confirmAction ?? ''] || actionConfig.reject;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="text-lg font-semibold mb-2"
          style={{ color: brand.colors.secondary }}
        >
          {config.title}
        </h3>
        <p className="text-sm mb-6" style={{ color: brand.colors.textLight }}>
          {config.message}
        </p>
        <div className="flex gap-3 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium rounded-lg border"
            style={{
              borderColor: brand.colors.border,
              color: brand.colors.textLight,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={actionLoading === selectedUser?.id}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: config.buttonColor }}
          >
            {actionLoading === selectedUser?.id ? 'Processing...' : config.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

interface AcceptanceModalProps {
  acceptanceUser: UserRow | null;
  record: UserLegalAcceptance | null;
  loading: boolean;
  error: string | null;
  brand: BrandConfig;
  onClose: () => void;
}

// Renders one document's acceptance pair inside the drill-in. Nulls are
// first-class here: an unrecorded side shows an explicit em dash, never a
// zero and never a missing cell.
const AcceptanceRow: React.FC<{
  documentName: string;
  version: number | null;
  acceptedAt: string | null;
}> = ({ documentName, version, acceptedAt }) => (
  <tr>
    <th scope="row" className="py-1.5 pr-3 text-left font-medium">
      {documentName}
    </th>
    <td className="py-1.5 pr-3">{version === null ? '—' : `v${version}`}</td>
    <td className="py-1.5">{acceptedAt ? new Date(acceptedAt).toLocaleString() : '—'}</td>
  </tr>
);

// The per-user acceptance drill-in (SOR-364). Acceptance belongs to the
// user's ACCOUNT, so the modal names the account the record came from; a user
// with no account membership and an account that never accepted are distinct
// renderings. `if (!acceptanceUser) return null` keeps the dialog out of the
// document until an operator asks for it.
const AcceptanceModal: React.FC<AcceptanceModalProps> = ({
  acceptanceUser,
  record,
  loading,
  error,
  brand,
  onClose,
}) => {
  if (!acceptanceUser) return null;

  const accepted = record?.accepted;
  const noneRecorded =
    !accepted ||
    (accepted.termsVersion === null &&
      accepted.termsAcceptedAt === null &&
      accepted.privacyVersion === null &&
      accepted.privacyAcceptedAt === null);

  // Read with the same tolerance `accepted` above gets, and for the same
  // reason: portal and api ship as separate Cloud Deploy pipelines, so a new
  // bundle can be served against an api revision that predates the field. An
  // absent block reads as "never dismissed" — the no-notice leg — rather than
  // throwing in the render.
  const dismissedNotice = record?.dismissedNotice ?? { version: null, dismissedAt: null };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="user-acceptance-title"
        data-testid="user-acceptance-modal"
        className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="user-acceptance-title" className="text-lg font-semibold mb-2" style={{ color: brand.colors.secondary }}>
          Legal Acceptance Record
        </h2>
        <p className="text-sm mb-4" style={{ color: brand.colors.textLight }}>
          {acceptanceUser.name || acceptanceUser.email}
        </p>

        {loading && (
          <p className="text-sm mb-6" data-testid="acceptance-loading" style={{ color: brand.colors.textLight }}>
            Loading acceptance record…
          </p>
        )}

        {!loading && error && (
          <p className="text-sm mb-6" data-testid="acceptance-error" style={{ color: brand.colors.error || '#EF4444' }}>
            Error loading acceptance record: {error}
          </p>
        )}

        {!loading && !error && record && (
          <>
            <p className="text-sm mb-4" style={{ color: brand.colors.text }}>
              {record.account ? (
                <>
                  Account: <span className="font-medium">{record.account.name}</span> (
                  {record.account.accountNumber})
                </>
              ) : (
                <span data-testid="acceptance-no-account">This user has no account membership.</span>
              )}
            </p>

            {noneRecorded ? (
              <div
                data-testid="acceptance-none-recorded"
                className="mb-6 p-4 rounded-lg text-sm"
                style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
              >
                No acceptance recorded — this account has not accepted the terms of service or the
                privacy policy.
              </div>
            ) : (
              <table data-testid="acceptance-recorded" className="w-full text-sm mb-6" style={{ color: brand.colors.text }}>
                <thead>
                  <tr className="border-b" style={{ borderColor: brand.colors.border }}>
                    <th scope="col" className="py-1.5 pr-3 text-left font-medium">
                      Document
                    </th>
                    <th scope="col" className="py-1.5 pr-3 text-left font-medium">
                      Version
                    </th>
                    <th scope="col" className="py-1.5 text-left font-medium">
                      Accepted At
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <AcceptanceRow documentName="Terms of Service" version={accepted.termsVersion} acceptedAt={accepted.termsAcceptedAt} />
                  <AcceptanceRow documentName="Privacy Policy" version={accepted.privacyVersion} acceptedAt={accepted.privacyAcceptedAt} />
                </tbody>
              </table>
            )}

            {/* The Terms-notice evidence (SOR-457) renders OUTSIDE the
                acceptance ternary above: it is an independent fact, so both
                its legs must be reachable for an account with no recorded
                acceptance at all. The two legs are mutually exclusive by
                construction — they key off the same `version` read — and the
                no-notice leg carries its own testid, never the acceptance
                box's `acceptance-none-recorded`. */}
            <div className="mb-6">
              <h3 className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Terms Notice
              </h3>
              {dismissedNotice.version !== null ? (
                <dl
                  data-testid="notice-dismissal-recorded"
                  className="text-sm"
                  style={{ color: brand.colors.text }}
                >
                  <div className="flex gap-2">
                    <dt className="font-medium">Last dismissed</dt>
                    <dd>{`v${dismissedNotice.version}`}</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="font-medium">Dismissed at</dt>
                    <dd>
                      {dismissedNotice.dismissedAt
                        ? new Date(dismissedNotice.dismissedAt).toLocaleString()
                        : '—'}
                    </dd>
                  </div>
                </dl>
              ) : (
                <p
                  data-testid="notice-none-recorded"
                  className="p-4 rounded-lg text-sm"
                  style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.textLight }}
                >
                  No Terms notice dismissal recorded — this account has not dismissed a Terms
                  notice.
                </p>
              )}
            </div>
          </>
        )}

        <div className="flex gap-3 justify-end">
          <button
            data-testid="user-acceptance-close"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded-lg border"
            style={{
              borderColor: brand.colors.border,
              color: brand.colors.textLight,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const AdminUsersList: React.FC<AdminUsersListProps> = ({ activeSubSection, onSubSectionChange, brand, Icons }) => {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<UserAction | null>(null);
  const [isTableLoading, setIsTableLoading] = useState<boolean>(true); // Track table loading state
  const [acceptanceUser, setAcceptanceUser] = useState<UserRow | null>(null);
  const [acceptanceRecord, setAcceptanceRecord] = useState<UserLegalAcceptance | null>(null);
  const [acceptanceLoading, setAcceptanceLoading] = useState<boolean>(false);
  const [acceptanceError, setAcceptanceError] = useState<string | null>(null);
  // Bumped by every drill-in open and close. A response paints only while its
  // own open is still the latest act, so two drill-ins opened inside one
  // response window can never misattribute — the slower response would
  // otherwise paint the first row's record (and clear the second row's
  // loading state) under the second row's name.
  const acceptanceRequestRef = useRef<number>(0);

  const {
    getUsers,
    getPendingUsers,
    approveUser,
    rejectUser,
    suspendUser,
    reactivateUser,
    getUserLegalAcceptance,
    loading: _loading,
    error,
  } = useAdminUsersApi();

  const subNavItems = [
    { key: 'pending', label: 'Pending Approval', count: pendingCount },
    { key: 'approved', label: 'Approved' },
    { key: 'suspended', label: 'Suspended' },
    { key: 'all', label: 'All Users' },
  ];

  // Clear table and show skeleton immediately when tab changes
  /* eslint-disable @eslint-react/set-state-in-effect -- reset UI before async refetch on tab change */
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    setUsers([]);
    setIsTableLoading(true);
  }, [activeSubSection]);
  /* eslint-enable @eslint-react/set-state-in-effect */

  // Fetch users based on active section
  const fetchUsers = useCallback(async () => {
    setIsTableLoading(true);
    try {
      let result;
      if (activeSubSection === 'pending') {
        result = await getPendingUsers();
      } else {
        result = await getUsers((activeSubSection || 'all') as 'pending' | 'approved' | 'suspended' | 'all');
      }
      setUsers(result.users || []);
      setTotalCount(result.pagination?.total || result.users?.length || 0);
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setIsTableLoading(false);
    }
  }, [activeSubSection, getUsers, getPendingUsers]);

  // Fetch pending count for badge
  const fetchPendingCount = useCallback(async () => {
    try {
      const result = await getPendingUsers();
      setPendingCount(result.users?.length || 0);
    } catch (err) {
      console.error('Error fetching pending count:', err);
    }
  }, [getPendingUsers]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- TODO(deps-2026-07): newly detected by react-hooks bump, burn-down separately
    fetchUsers();
    fetchPendingCount();
  }, [fetchUsers, fetchPendingCount]);

  // Action handlers
  const handleApprove = async (userId) => {
    setActionLoading(userId);
    try {
      await approveUser(userId);
      await fetchUsers();
      await fetchPendingCount();
    } catch (err) {
      console.error('Error approving user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId) => {
    setActionLoading(userId);
    try {
      await rejectUser(userId);
      await fetchUsers();
      await fetchPendingCount();
    } catch (err) {
      console.error('Error rejecting user:', err);
    } finally {
      setActionLoading(null);
      setShowConfirmModal(false);
      setSelectedUser(null);
    }
  };

  const handleSuspend = async (userId) => {
    setActionLoading(userId);
    try {
      await suspendUser(userId);
      await fetchUsers();
    } catch (err) {
      console.error('Error suspending user:', err);
    } finally {
      setActionLoading(null);
      setShowConfirmModal(false);
      setSelectedUser(null);
    }
  };

  const handleReactivate = async (userId) => {
    setActionLoading(userId);
    try {
      await reactivateUser(userId);
      await fetchUsers();
    } catch (err) {
      console.error('Error reactivating user:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirmModal = (user, action) => {
    setSelectedUser(user);
    setConfirmAction(action);
    setShowConfirmModal(true);
  };

  // Fetch the acceptance record at drill-in time — one read per open, never
  // prefetched for the whole table.
  const openAcceptanceModal = async (user) => {
    const requestId = ++acceptanceRequestRef.current;
    setAcceptanceUser(user);
    setAcceptanceRecord(null);
    setAcceptanceError(null);
    setAcceptanceLoading(true);
    try {
      const result = await getUserLegalAcceptance(user.id);
      if (acceptanceRequestRef.current === requestId) {
        setAcceptanceRecord(result);
      }
    } catch (err) {
      if (acceptanceRequestRef.current === requestId) {
        console.error('Error fetching legal acceptance:', err);
        setAcceptanceError(err instanceof Error ? err.message : 'Unknown error');
      }
    } finally {
      if (acceptanceRequestRef.current === requestId) {
        setAcceptanceLoading(false);
      }
    }
  };

  const closeAcceptanceModal = () => {
    acceptanceRequestRef.current += 1;
    setAcceptanceUser(null);
    setAcceptanceRecord(null);
    setAcceptanceError(null);
  };

  const executeConfirmAction = () => {
    if (!selectedUser || !confirmAction) return;

    if (confirmAction === 'reject') {
      handleReject(selectedUser.id);
    } else if (confirmAction === 'suspend') {
      handleSuspend(selectedUser.id);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      filterable: true,
      render: (value, row) => (
        <div className="flex flex-col">
          <div className="text-sm font-medium" style={{ color: brand.colors.text }}>
            {value || 'No name'}
          </div>
          <div className="text-xs" style={{ color: brand.colors.textLight }}>
            {row.email}
          </div>
        </div>
      ),
    },
    {
      key: 'appMetadata',
      label: 'Details',
      render: (metadata, row) => (
        <div className="flex flex-col text-xs" style={{ color: brand.colors.textLight }}>
          {metadata?.jobRole && <span>Role: {metadata.jobRole}</span>}
          {row.organizations?.length > 0 ? (
            <span>Firm: {row.organizations[0].displayName}</span>
          ) : metadata?.firmName ? (
            <span>Firm: {metadata.firmName}</span>
          ) : metadata?.usageType === 'self' ? (
            <span>Type: Solo</span>
          ) : metadata?.usageType ? (
            <span>Type: {metadata.usageType}</span>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'suspended', label: 'Suspended' },
      ],
      render: (value) => <UserStatusBadge status={value} />,
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (value) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {value ? new Date(value).toLocaleDateString() : '-'}
        </span>
      ),
    },
    {
      key: 'lastLogin',
      label: 'Last Login',
      sortable: true,
      render: (value) => (
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          {value ? new Date(value).toLocaleDateString() : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '220px',
      render: (_, row) => {
        const isLoading = actionLoading === row.id;

        // The acceptance drill-in rides every row regardless of status — a
        // pending user is exactly the population whose account may hold no
        // recorded acceptance.
        return (
          <div className="flex flex-wrap gap-2">
            {row.status === 'pending' && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleApprove(row.id);
                  }}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: brand.colors.success || '#10B981',
                    color: 'white',
                  }}
                >
                  {isLoading ? 'Processing...' : 'Approve'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openConfirmModal(row, 'reject');
                  }}
                  disabled={isLoading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: brand.colors.error || '#EF4444',
                    color: 'white',
                  }}
                >
                  Reject
                </button>
              </>
            )}

            {row.status === 'approved' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openConfirmModal(row, 'suspend');
                }}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border disabled:opacity-50"
                style={{
                  borderColor: brand.colors.warning || '#F59E0B',
                  color: brand.colors.warning || '#F59E0B',
                }}
              >
                {isLoading ? 'Processing...' : 'Suspend'}
              </button>
            )}

            {row.status === 'suspended' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleReactivate(row.id);
                }}
                disabled={isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                style={{
                  backgroundColor: brand.colors.primary,
                  color: brand.colors.onPrimary,
                }}
              >
                {isLoading ? 'Processing...' : 'Reactivate'}
              </button>
            )}

            <button
              data-testid={`user-acceptance-${row.id}`}
              onClick={(e) => {
                e.stopPropagation();
                openAcceptanceModal(row);
              }}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors border"
              style={{
                borderColor: brand.colors.primary,
                color: brand.colors.link,
              }}
            >
              Acceptance
            </button>
          </div>
        );
      },
    },
  ];

  const getSectionTitle = () => {
    switch (activeSubSection) {
      case 'pending':
        return 'Pending Approval';
      case 'approved':
        return 'Approved Users';
      case 'suspended':
        return 'Suspended Users';
      case 'all':
      default:
        return 'All Users';
    }
  };

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden"
      style={{ backgroundColor: brand.colors.bgLight }}
    >
      <AdminHeader title={getSectionTitle()} brand={brand} Icons={Icons} />

      <AdminSubNav
        items={subNavItems}
        activeItem={activeSubSection || 'pending'}
        onItemChange={onSubSectionChange}
        brand={brand}
      />

      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div
            className="mb-4 p-4 rounded-lg"
            style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
          >
            Error loading users: {error}
          </div>
        )}

        {!isTableLoading && users.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Icons.User
                className="w-16 h-16 mx-auto mb-4"
                style={{ color: brand.colors.textMuted }}
              />
              <h2
                className="text-lg font-medium mb-2"
                style={{ color: brand.colors.secondary }}
              >
                No users found
              </h2>
              <p style={{ color: brand.colors.textLight }}>
                {activeSubSection === 'pending'
                  ? 'No users are pending approval.'
                  : 'No users match the current filter.'}
              </p>
            </div>
          </div>
        ) : (
          <AdminDataTable
            columns={columns}
            data={users}
            totalCount={isTableLoading ? 0 : totalCount}
            showCheckboxes={false}
            showPagination={true}
            onRowClick={(row) => console.log('User clicked:', row)}
            brand={brand}
            Icons={Icons}
            isLoading={isTableLoading}
            skeletonRows={5}
          />
        )}
      </div>

      <ConfirmModal
        showConfirmModal={showConfirmModal}
        confirmAction={confirmAction}
        selectedUser={selectedUser}
        actionLoading={actionLoading}
        brand={brand}
        onCancel={() => {
          setShowConfirmModal(false);
          setSelectedUser(null);
          setConfirmAction(null);
        }}
        onConfirm={executeConfirmAction}
      />

      <AcceptanceModal
        acceptanceUser={acceptanceUser}
        record={acceptanceRecord}
        loading={acceptanceLoading}
        error={acceptanceError}
        brand={brand}
        onClose={closeAcceptanceModal}
      />
    </div>
  );
};

export default AdminUsersList;
